/**
 * System API Service
 * Handles system bootstrap and admin operations
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Response type for createTestAccounts
 */
export interface CreateTestAccountsResponse {
  success: true;
  data: {
    created: number;
    accounts: Array<{
      email: string;
      role: string;
      plan: string;
      id: string;
    }>;
  };
}

/**
 * Response type for clearTables
 */
export interface ClearTablesResponse {
  success: true;
  data: {
    cleared: number;
    tables: string[];
  };
}

/**
 * Response type for seedData
 */
export interface SeedDataResponse {
  success: true;
  data: {
    imported: boolean;
    message: string;
    details?: {
      baseData: number;
      memberData: number;
    };
  };
}

/**
 * Response type for resetAll
 */
export interface ResetAllResponse {
  success: boolean;
  data: {
    success: boolean;
    steps: Array<{
      name: string;
      status: 'success' | 'error';
      message: string;
    }>;
  };
}

/**
 * Create test accounts for development
 */
export async function createTestAccounts(): Promise<CreateTestAccountsResponse> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const url = `${API_BASE}/api/admin/system/bootstrap/accounts`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: '未知錯誤' } }));
    throw new Error(error.error?.message || '建立測試帳號失敗');
  }

  return response.json();
}

/**
 * Clear database tables
 */
export async function clearTables(): Promise<ClearTablesResponse> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const url = `${API_BASE}/api/admin/system/bootstrap/clear`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: '未知錯誤' } }));
    throw new Error(error.error?.message || '清除資料表失敗');
  }

  return response.json();
}

/**
 * Seed database with initial data
 */
export async function seedData(): Promise<SeedDataResponse> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const url = `${API_BASE}/api/admin/system/bootstrap/seed`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: '未知錯誤' } }));
    throw new Error(error.error?.message || '匯入種子資料失敗');
  }

  return response.json();
}

/**
 * Reset all system data (clear tables, seed data, create test accounts)
 */
export async function resetAll(): Promise<ResetAllResponse> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('未登入');
  }

  const url = `${API_BASE}/api/admin/system/bootstrap/reset`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: '未知錯誤' } }));
    throw new Error(error.error?.message || '重置系統失敗');
  }

  return response.json();
}
